import EditIcon from '@mui/icons-material/Edit';
import {
    Card,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import Label from 'src/components/Label';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { PostDetailsHelper } from 'src/utils/postDetailsHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import DeleteIcon from '../../assets/deletecomment.svg';
import UserListHead from '../../sections/@dashboard/user/UserListHead';
import NoPostFound from '../publishedPosts/noPost.component';
import RolesToolbar from './rolesToolbar';
import UpdateRole from './updateRole.component';

const ManageRoles = () => {
  const { t: translate } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [updateRole, setUpadateRole] = useState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const { showToast } = useToaster();
  const [toBeDeletedId, setToBeDeletedId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openUpdateRoleModel, setUpdateRoleModel] = useState(false);
  const [height, setHeight] = useState(window.innerHeight - 250);

  useEffect(() => {
    getAllRoles();
  }, []);

  const getAllRoles = () => {
    setLoader(true);
    let postProps = {
      url: userModuleURL.getAllRoles,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
  };

  const successCallback = (data, message) => {
    message && showToast(message, 's');
    setLoader(false);
    setRoles(data);
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoader(false);
  };

  const deleteSuccessCallback = (data, message) => {
    message && showToast(message, 's');
    setLoader(false);
    getAllRoles();
  };

  const handleDelete = (id) => {
    setToBeDeletedId(id);
    setConfirmOpen(true);
  };

  const handleUpdate = (data) => {
    setUpadateRole(data);
    setUpdateRoleModel(true);
  };

  const handleClose = () => {
    setUpdateRoleModel(false);
  };

  const confirmDelete = () => {
    setConfirmOpen(false);
    let roleprops = {
      role_id: toBeDeletedId,
    };
    let postProps = {
      url: userModuleURL.deleteRole,
      body: roleprops,
      successCallback: deleteSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getRolePermissionView = (rolePermission) => {
    return rolePermission?.role_permissions
      ?.filter((a) => a.has_permission)
      .map((permission) => {
        return (
          <Label color="primary" style={{ margin: 0.5 }}>
            {permission.permission_display_name}
          </Label>
        );
      });
  };

  const handleRoleCreated = (message) => {
    message && showToast(message, 's');
    setUpdateRoleModel(false);
    getAllRoles();
  };

  return (
    <>
      <Helmet>
        <title>{translate('manage_brand.manage_roles.title')}</title>
      </Helmet>
      {loader ? (
        <Spinner />
      ) : (
        <>
          {roles && roles.length > 0 ? (
            <Card sx={{ m: 1 }}>
              <RolesToolbar handleSuccess={handleRoleCreated} />
              <TableContainer
                sx={{
                  minHeight: 'calc(100vh - 305px)',
                  maxHeight: 'calc(100vh - 305px)',
                }}
              >
                <Table aria-label="collapsible table" stickyHeader>
                  <UserListHead
                    headLabel={[
                      {
                        id: 'role',
                        label: translate('common.table.role'),
                        width: '15%',
                        alignCenter: false,
                      },
                      {
                        id: 'Permissions',
                        label: translate('common.table.permissions'),
                        width: '40%',
                        alignRight: false,
                      },
                      {
                        id: 'connectedProfiles',
                        label: translate('manage_brand.manage_roles.table.connected_profiles'),
                        width: '35%',
                        alignRight: false,
                      },
                      {
                        id: 'action',
                        label: translate('common.action'),
                        width: '10%',
                        alignCenter: true,
                      },
                    ]}
                    rowCount={roles.length}
                    isShowCheckbox={false}
                    style={{ whiteSpace: 'nowrap' }}
                  />
                  <TableBody>
                    {roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((role) => (
                      <TableRow hover key={role.id} tabIndex={-1}>
                        <TableCell align="left">{role.role_name}</TableCell>
                        <TableCell align="left">{getRolePermissionView(role)}</TableCell>
                        <TableCell align="left">
                          {PostDetailsHelper.getSocialMediaViewWithTooltip(role.connected_profiles)}
                        </TableCell>
                        <TableCell align="center">
                          {!role.is_default && (
                            <IconButton
                              onClick={() => {
                                handleDelete(role.id);
                              }}
                              size="large"
                            >
                              <img src={DeleteIcon} style={{ width: 23, height: 23 }} />
                            </IconButton>
                          )}
                          {!role.is_default && (
                            <IconButton
                              onClick={() => {
                                handleUpdate(role);
                              }}
                              size="large"
                            >
                              <EditIcon sx={{ color: 'primary.main' }} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 15, 25]}
                count={roles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmDelete}
                title={translate('common.confirm_delete_title')}
                message={`${translate('manage_brand.manage_roles.confirm_delete_message')}?`}
              />
              {openUpdateRoleModel && (
                <UpdateRole handleClose={handleClose} updateRole={updateRole} handleSuccess={handleRoleCreated} />
              )}
            </Card>
          ) : (
            <Card sx={{ minHeight: 'calc(100vh - 150px)', maxHeight: 'calc(100vh - 150px)' }}>
              <RolesToolbar handleSuccess={handleRoleCreated} />
              <NoPostFound height={height} />
            </Card>
          )}
        </>
      )}
    </>
  );
};

export default ManageRoles;
